import { useId, useState, type InputHTMLAttributes, type ReactNode } from "react";
import { Eye, EyeOff } from "lucide-react";
import { cn } from "@/lib/cn";

/* A labelled input that matches the cream/ink token system. */
export function Field({
  label,
  hint,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: string; hint?: ReactNode }) {
  const id = useId();
  return (
    <div className={className}>
      <div className="mb-1.5 flex items-baseline justify-between">
        <label htmlFor={id} className="text-[13px] font-medium text-ink">
          {label}
        </label>
        {hint}
      </div>
      <input
        id={id}
        className="h-12 w-full rounded-btn border border-hairline bg-paper px-3.5 text-[15px] text-ink-strong outline-none transition-colors duration-200 ease-out-soft placeholder:text-subtle focus:border-accent"
        {...rest}
      />
    </div>
  );
}

/* Password field with a quiet show/hide toggle (44px hit target). */
export function PasswordField({
  label,
  hint,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  label: string;
  hint?: ReactNode;
}) {
  const id = useId();
  const [show, setShow] = useState(false);
  return (
    <div className={className}>
      <div className="mb-1.5 flex items-baseline justify-between">
        <label htmlFor={id} className="text-[13px] font-medium text-ink">
          {label}
        </label>
        {hint}
      </div>
      <div className="relative">
        <input
          id={id}
          type={show ? "text" : "password"}
          className="h-12 w-full rounded-btn border border-hairline bg-paper pl-3.5 pr-12 text-[15px] text-ink-strong outline-none transition-colors duration-200 ease-out-soft placeholder:text-subtle focus:border-accent"
          {...rest}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? "Hide password" : "Show password"}
          className="absolute right-1.5 top-1/2 grid h-9 w-9 -translate-y-1/2 place-items-center rounded-btn text-subtle transition-colors hover:text-ink"
        >
          {show ? (
            <EyeOff className="h-[18px] w-[18px]" strokeWidth={1.5} />
          ) : (
            <Eye className="h-[18px] w-[18px]" strokeWidth={1.5} />
          )}
        </button>
      </div>
    </div>
  );
}

/* Remember-me — minimal warm checkbox, no coral (coral stays scarce). */
export function RememberMe() {
  const [on, setOn] = useState(true);
  return (
    <button
      type="button"
      onClick={() => setOn((v) => !v)}
      className="group inline-flex items-center gap-2.5 text-[13px] text-muted hover:text-ink"
    >
      <span
        className={cn(
          "grid h-[18px] w-[18px] place-items-center rounded-[5px] border transition-colors",
          on ? "border-ink-strong bg-ink-strong" : "border-hairline bg-paper",
        )}
      >
        {on && (
          <svg viewBox="0 0 12 12" className="h-3 w-3 text-canvas" fill="none">
            <path
              d="M2.5 6.2 5 8.5 9.5 3.8"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </span>
      Remember me
    </button>
  );
}

/* Inline brand SVGs so social buttons stay self-contained (mock). */
function GoogleMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px]" aria-hidden>
      <path
        fill="#4285F4"
        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.27-4.74 3.27-8.1Z"
      />
      <path
        fill="#34A853"
        d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
      />
      <path
        fill="#FBBC05"
        d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"
      />
    </svg>
  );
}

function GithubMark() {
  return (
    <svg viewBox="0 0 24 24" className="h-[18px] w-[18px] fill-ink-strong" aria-hidden>
      <path d="M12 1.5a10.5 10.5 0 0 0-3.32 20.46c.53.1.72-.23.72-.5v-1.78c-2.92.63-3.54-1.4-3.54-1.4-.48-1.22-1.17-1.55-1.17-1.55-.96-.65.07-.64.07-.64 1.06.08 1.62 1.09 1.62 1.09.94 1.6 2.46 1.14 3.06.87.1-.68.37-1.14.67-1.4-2.33-.27-4.78-1.17-4.78-5.18 0-1.15.41-2.08 1.08-2.82-.11-.27-.47-1.34.1-2.79 0 0 .88-.28 2.88 1.07a10 10 0 0 1 5.24 0c2-1.35 2.88-1.07 2.88-1.07.57 1.45.21 2.52.1 2.79.67.74 1.08 1.67 1.08 2.82 0 4.02-2.46 4.9-4.8 5.16.38.33.71.97.71 1.96v2.9c0 .28.19.61.73.5A10.5 10.5 0 0 0 12 1.5Z" />
    </svg>
  );
}

/** Social sign-in. ZITADEL brokers the IdPs, so both buttons start the same
 *  OIDC redirect (the provider is chosen on ZITADEL's hosted login). */
export function SocialButtons({ onSelect }: { onSelect?: () => void }) {
  const base =
    "inline-flex h-12 w-full items-center justify-center gap-2.5 rounded-btn border border-hairline bg-paper text-[14px] font-medium text-ink transition-colors duration-200 ease-out-soft hover:bg-paper-2 dark:hover:bg-white/5";
  return (
    <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
      <button type="button" className={base} onClick={onSelect}>
        <GoogleMark />
        Continue with Google
      </button>
      <button type="button" className={base} onClick={onSelect}>
        <GithubMark />
        Continue with GitHub
      </button>
    </div>
  );
}

/* "or" divider with hairlines. */
export function OrDivider() {
  return (
    <div className="my-5 flex items-center gap-3">
      <span className="h-px flex-1 bg-hairline" />
      <span className="font-mono text-[10px] uppercase tracking-wide text-subtle">or</span>
      <span className="h-px flex-1 bg-hairline" />
    </div>
  );
}
